/**
 * Whirlpool Renderer
 * 
 * Renders whirlpool entries in the overworld and handles the visual
 * representation of whirlpool instances (pocket dimensions).
 * 
 * Whirlpools are displayed as 1x1 tile animated vortexes that players
 * can sail into to enter dangerous pocket dimensions with high rewards.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';

// ============ TYPES ============

export interface WhirlpoolEntryData {
  id: string;
  instanceId: string;
  tileX: number;
  tileY: number;
  worldX: number;
  worldY: number;
  class: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
  status: 'stable' | 'unstable' | 'critical' | 'collapsed';
  spawnedAt: number;
  expiresAt: number;
  massLimit: number;
  massUsed: number;
  discoveredBy: string | null;
  rotationSpeed: number;
}

export interface WhirlpoolInstanceData {
  id: string;
  entryId: string;
  class: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
  className: string;
  effect: string;
  effectModifiers: {
    armorMultiplier: number;
    shieldMultiplier: number;
    damageMultiplier: number;
    speedMultiplier: number;
    repairMultiplier: number;
    signatureMultiplier: number;
    description: string;
  };
  width: number;
  height: number;
  islands: Array<{
    id: string;
    x: number;
    y: number;
    radius: number;
    biome: string;
  }>;
  resourceNodes: Array<{
    id: string;
    type: string;
    tileX: number;
    tileY: number;
    worldX: number;
    worldY: number;
    amount: number;
    maxAmount: number;
    definition: {
      name: string;
      description: string;
      baseValue: number;
    };
  }>;
  hazards: Array<{
    id: string;
    type: string;
    tileX: number;
    tileY: number;
    radius: number;
    active: boolean;
  }>;
  npcs: Array<{
    id: string;
    name: string;
    level: number;
    x: number;
    y: number;
    behavior: string;
    telegraphs?: Array<{
      id: string;
      bossId: string;
      type: 'melee_sweep' | 'ranged_volley' | 'aoe_blast' | 'targeted_strike' | 'area_denial';
      centerX: number;
      centerY: number;
      radius: number;
      activatesAt: number;
      duration: number;
      color: number;
    }>;
  }>;
  exitPoint: {
    tileX: number;
    tileY: number;
    worldX: number;
    worldY: number;
    stable: boolean;
  };
  playerCount: number;
  lootMultiplier: number;
  xpMultiplier: number;
}

interface WhirlpoolSprite {
  container: Phaser.GameObjects.Container;
  aura: Phaser.GameObjects.Graphics;
  graphics: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  data: WhirlpoolEntryData;
  rotation: number;
}

interface WhirlpoolRendererCallbacks {
  onWhirlpoolClick: (entry: WhirlpoolEntryData) => void;
  onWhirlpoolRightClick: (entry: WhirlpoolEntryData, screenX: number, screenY: number) => void;
  onWhirlpoolHover: (entry: WhirlpoolEntryData, screenX: number, screenY: number) => void;
  onWhirlpoolHoverEnd: () => void;
  onWhirlpoolResourceClick?: (resourceId: string, worldX: number, worldY: number) => void;
  onWhirlpoolExitClick?: (worldX: number, worldY: number) => void;
}

// ============ CLASS COLORS ============

const WHIRLPOOL_CLASS_COLORS: Record<string, number> = {
  C1: 0x4fc3f7, // Light blue - safe
  C2: 0x29b6f6, // Blue
  C3: 0x7e57c2, // Purple
  C4: 0xab47bc, // Magenta
  C5: 0xef5350, // Red
  C6: 0xd32f2f, // Dark red - deadly
};

const WHIRLPOOL_STATUS_ALPHA: Record<string, number> = {
  stable: 1.0,
  unstable: 0.8,
  critical: 0.6,
  collapsed: 0.3,
};

// Resource node colors (matching StructureRenderer)
const RESOURCE_COLORS: Record<string, { fill: number; border: number; icon: string }> = {
  timber: { fill: 0x228b22, border: 0x145214, icon: '[T]' },
  ore: { fill: 0x708090, border: 0x4a5a64, icon: '[O]' },
  sulfur: { fill: 0xffff00, border: 0xcccc00, icon: '[S]' },
  whale_oil: { fill: 0x4169e1, border: 0x2a4a9a, icon: '[W]' },
  herbs: { fill: 0x32cd32, border: 0x228b22, icon: '[H]' },
  gems: { fill: 0x9932cc, border: 0x6a1f8a, icon: '[G]' },
  nullstone_shard: { fill: 0x4fc3f7, border: 0x29b6f6, icon: '[N]' },
  ancient_relic: { fill: 0xffd700, border: 0xcc8400, icon: '[A]' },
  void_crystal: { fill: 0x9932cc, border: 0x6a1f8a, icon: '[V]' },
};

// Biome colors matching main game IslandRenderer
const BIOME_COLORS: Record<string, { shore: number; main: number; accent: number }> = {
  void: { shore: 0x2d1b4e, main: 0x1a0a2e, accent: 0x3d2b5e },
  crystal: { shore: 0x9b8bce, main: 0x7b68ee, accent: 0x8b78de },
  storm: { shore: 0x5a6578, main: 0x4a5568, accent: 0x6a7588 },
  ancient: { shore: 0x9b8365, main: 0x8b7355, accent: 0xab9375 },
  corrupted: { shore: 0x5a1e1e, main: 0x4a0e0e, accent: 0x6a2e2e },
  beach: { shore: 0xf5deb3, main: 0xc2b280, accent: 0xd4c896 },
  forest: { shore: 0xc2b280, main: 0x228b22, accent: 0x2e8b2e },
  mountain: { shore: 0x8b8b83, main: 0x696969, accent: 0x808080 },
};

// ============ RENDERER CLASS ============

export class WhirlpoolRenderer {
  private scene: Phaser.Scene;
  private callbacks: WhirlpoolRendererCallbacks;
  private whirlpools: Map<string, WhirlpoolSprite> = new Map();
  private tooltip: Phaser.GameObjects.Container | null = null;
  private isInsideWhirlpool: boolean = false;
  private currentInstance: WhirlpoolInstanceData | null = null;
  private overworldAnimPhase: number = 0;
  
  // Instance rendering
  private instanceContainer: Phaser.GameObjects.Container | null = null;
  private exitMarker: Phaser.GameObjects.Container | null = null;
  private resourceMarkers: Map<string, Phaser.GameObjects.Container> = new Map();
  private hazardGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  // NPCs are rendered by ShipRenderer (real NPC ships spawned server-side)

  private resourceAnimPhase: number = 0;
  private resourceAnimTimer: Phaser.Time.TimerEvent | null = null;

  constructor(scene: Phaser.Scene, callbacks: WhirlpoolRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  // ============ OVERWORLD RENDERING ============

  renderWhirlpools(entries: WhirlpoolEntryData[]): void {
    // Defensive check: ensure entries is an array
    if (!Array.isArray(entries)) {
      console.warn('⚠️ renderWhirlpools received non-array entries:', entries);
      return;
    }

    // Remove whirlpools that no longer exist
    const currentIds = new Set(entries.map(e => e.id));
    Array.from(this.whirlpools.entries()).forEach(([id, sprite]) => {
      if (!currentIds.has(id)) {
        sprite.container.destroy();
        this.whirlpools.delete(id);
      }
    });

    // Add or update whirlpools
    for (const entry of entries) {
      if (this.whirlpools.has(entry.id)) {
        this.updateWhirlpool(entry);
      } else {
        this.createWhirlpool(entry);
      }
    }
  }

  private createWhirlpool(entry: WhirlpoolEntryData): void {
    const container = this.scene.add.container(entry.worldX, entry.worldY);
    container.setDepth(50); // Above water, below ships

    // Aura graphics (surrounding tiles)
    const aura = this.scene.add.graphics();
    this.drawAura(aura, entry, this.scene.time.now);
    container.add(aura);

    // Create vortex graphics
    const graphics = this.scene.add.graphics();
    this.drawVortex(graphics, entry);
    container.add(graphics);

    // Create class label
    const label = this.scene.add.text(0, -TILE_SIZE, entry.class, {
      fontSize: '10px',
      fontFamily: 'monospace',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    label.setOrigin(0.5, 1);
    container.add(label);

    // Make interactive
    const hitArea = new Phaser.Geom.Rectangle(
      -TILE_SIZE / 2,
      -TILE_SIZE / 2,
      TILE_SIZE,
      TILE_SIZE
    );
    container.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Prevent this click from also triggering the global click-to-move handler.
      // Phaser exposes the underlying DOM event on pointer.event.
      const rawEvent = pointer.event as MouseEvent | PointerEvent | undefined;
      rawEvent?.preventDefault?.();
      rawEvent?.stopPropagation?.();

      if (pointer.rightButtonDown()) {
        this.callbacks.onWhirlpoolRightClick(entry, pointer.x, pointer.y);
      } else {
        this.callbacks.onWhirlpoolClick(entry);
      }
    });

    container.on('pointerover', (pointer: Phaser.Input.Pointer) => {
      this.callbacks.onWhirlpoolHover(entry, pointer.x, pointer.y);
    });

    container.on('pointerout', () => {
      this.callbacks.onWhirlpoolHoverEnd();
    });

    const sprite: WhirlpoolSprite = {
      container,
      aura,
      graphics,
      label,
      data: entry,
      rotation: 0,
    };

    this.whirlpools.set(entry.id, sprite);
  }

  private updateWhirlpool(entry: WhirlpoolEntryData): void {
    const sprite = this.whirlpools.get(entry.id);
    if (!sprite) return;

    sprite.data = entry;
    sprite.container.setPosition(entry.worldX, entry.worldY);
    sprite.label.setText(entry.class);

    // Redraw aura/vortex with updated status
    sprite.aura.clear();
    this.drawAura(sprite.aura, entry, this.scene.time.now);

    sprite.graphics.clear();
    this.drawVortex(sprite.graphics, entry);
  }

  private drawAura(graphics: Phaser.GameObjects.Graphics, entry: WhirlpoolEntryData, time: number): void {
    const color = WHIRLPOOL_CLASS_COLORS[entry.class] || 0x4fc3f7;
    const alpha = WHIRLPOOL_STATUS_ALPHA[entry.status] || 1.0;

    // Swirling storm effect: animated spiral pattern around the whirlpool
    const radiusTiles = 1;
    for (let ty = -radiusTiles; ty <= radiusTiles; ty++) {
      for (let tx = -radiusTiles; tx <= radiusTiles; tx++) {
        if (tx === 0 && ty === 0) continue;

        const dist = Math.abs(tx) + Math.abs(ty);
        const tileMinX = tx * TILE_SIZE - TILE_SIZE / 2;
        const tileMinY = ty * TILE_SIZE - TILE_SIZE / 2;

        // Swirling storm animation: rotating wave pattern
        const angle = Math.atan2(ty, tx);
        const rotationPhase = (time * 0.008 + angle) % (Math.PI * 2);
        const swirl = Math.sin(rotationPhase) * 0.5 + 0.5;
        
        const tileAlpha = alpha * (0.2 + swirl * 0.3) * (dist === 1 ? 1.0 : 0.6);

        // Draw swirling storm tiles
        graphics.fillStyle(color, tileAlpha);
        graphics.fillRect(tileMinX, tileMinY, TILE_SIZE, TILE_SIZE);

        // Animated storm streaks (rotating around center)
        const streakCount = 3;
        for (let i = 0; i < streakCount; i++) {
          const streakAngle = (rotationPhase + (i / streakCount) * Math.PI * 2) % (Math.PI * 2);
          const streakX = tileMinX + Math.cos(streakAngle) * (TILE_SIZE * 0.3);
          const streakY = tileMinY + Math.sin(streakAngle) * (TILE_SIZE * 0.3);
          
          const streakAlpha = tileAlpha * (0.5 + swirl * 0.5);
          graphics.fillStyle(color, streakAlpha);
          graphics.fillRect(streakX - 1, streakY - 1, 3, 3);
        }

        // Energy particles scattered in the tile
        const seed = (tx + 5) * 991 + (ty + 7) * 787 + Math.floor(rotationPhase * 10);
        const particleCount = 2;
        for (let p = 0; p < particleCount; p++) {
          const px = tileMinX + ((seed + p * 31) % (TILE_SIZE - 2));
          const py = tileMinY + ((seed + p * 53) % (TILE_SIZE - 2));
          graphics.fillStyle(0xffffff, tileAlpha * 0.4);
          graphics.fillRect(px, py, 2, 2);
        }
      }
    }
  }

  private drawVortex(graphics: Phaser.GameObjects.Graphics, entry: WhirlpoolEntryData): void {
    const color = WHIRLPOOL_CLASS_COLORS[entry.class] || 0x4fc3f7;
    const alpha = WHIRLPOOL_STATUS_ALPHA[entry.status] || 1.0;
    const size = TILE_SIZE;
    const half = Math.floor(size / 2);

    // Bright border only - simple square outline
    graphics.lineStyle(2, color, alpha);
    graphics.strokeRect(-half, -half, size, size);

    // Corner accent dots for visual interest
    const cornerSize = 2;
    graphics.fillStyle(color, alpha);
    graphics.fillRect(-half - 1, -half - 1, cornerSize, cornerSize);
    graphics.fillRect(half, -half - 1, cornerSize, cornerSize);
    graphics.fillRect(-half - 1, half, cornerSize, cornerSize);
    graphics.fillRect(half, half, cornerSize, cornerSize);

    // Status warning indicators
    if (entry.status === 'unstable' || entry.status === 'critical') {
      const warningColor = entry.status === 'critical' ? 0xff0000 : 0xffaa00;
      graphics.fillStyle(warningColor, 0.8);
      const warningSize = 3;
      // Top-left corner warning
      graphics.fillRect(-half, -half, warningSize, warningSize);
      // Top-right corner warning
      graphics.fillRect(half - warningSize, -half, warningSize, warningSize);
      // Bottom-left corner warning
      graphics.fillRect(-half, half - warningSize, warningSize, warningSize);
      // Bottom-right corner warning
      graphics.fillRect(half - warningSize, half - warningSize, warningSize, warningSize);
    }
  }

  update(delta: number): void {
    const time = this.scene.time.now;

    // Overworld animation: advance phase and redraw to avoid blurry rotation
    this.overworldAnimPhase = (this.overworldAnimPhase + 1) % 4;

    Array.from(this.whirlpools.values()).forEach(sprite => {
      sprite.aura.clear();
      this.drawAura(sprite.aura, sprite.data, time);
      sprite.graphics.clear();
      this.drawVortex(sprite.graphics, sprite.data);
    });

    // Update hazard animations if inside whirlpool
    if (this.isInsideWhirlpool && this.currentInstance) {
      this.updateHazardAnimations(time);

      if (this.exitMarker) {
        const aura = (this.exitMarker as any).aura as Phaser.GameObjects.Graphics | undefined;
        const graphics = (this.exitMarker as any).graphics as Phaser.GameObjects.Graphics | undefined;
        const exitPoint = (this.exitMarker as any).exitPoint as WhirlpoolInstanceData['exitPoint'] | undefined;
        if (aura && graphics && exitPoint) {
          aura.clear();
          this.drawExitAura(aura, exitPoint, time);
          graphics.clear();
          this.drawExitVortex(graphics, exitPoint, time);
        }
      }
    }
  }

  private drawExitAura(graphics: Phaser.GameObjects.Graphics, exitPoint: WhirlpoolInstanceData['exitPoint'], time: number): void {
    const color = 0x00ffff;
    const alpha = exitPoint.stable ? 1.0 : 0.6;

    const centerTileX = Math.floor(exitPoint.worldX / TILE_SIZE);
    const centerTileY = Math.floor(exitPoint.worldY / TILE_SIZE);
    const radiusTiles = 1;

    for (let ty = -radiusTiles; ty <= radiusTiles; ty++) {
      for (let tx = -radiusTiles; tx <= radiusTiles; tx++) {
        if (tx === 0 && ty === 0) continue;

        const tileMinX = (centerTileX + tx) * TILE_SIZE;
        const tileMinY = (centerTileY + ty) * TILE_SIZE;

        const angle = Math.atan2(ty, tx);
        const rotationPhase = (time * 0.008 + angle) % (Math.PI * 2);
        const swirl = Math.sin(rotationPhase) * 0.5 + 0.5;
        const tileAlpha = alpha * (0.15 + swirl * 0.25);

        graphics.fillStyle(color, tileAlpha);
        graphics.fillRect(tileMinX, tileMinY, TILE_SIZE, TILE_SIZE);

        const streakX = tileMinX + (TILE_SIZE / 2) + Math.cos(rotationPhase) * (TILE_SIZE * 0.2);
        const streakY = tileMinY + (TILE_SIZE / 2) + Math.sin(rotationPhase) * (TILE_SIZE * 0.2);
        graphics.fillStyle(0xffffff, tileAlpha * 0.4);
        graphics.fillRect(Math.floor(streakX) - 1, Math.floor(streakY) - 1, 2, 2);
      }
    }
  }

  private drawExitVortex(graphics: Phaser.GameObjects.Graphics, exitPoint: WhirlpoolInstanceData['exitPoint'], time: number): void {
    const color = 0x00ffff;
    const alpha = exitPoint.stable ? 1.0 : 0.6;
    const size = TILE_SIZE;
    const half = Math.floor(size / 2);

    const pulse = Math.sin(time * 0.004) * 0.15 + 0.85;

    graphics.lineStyle(2, color, alpha * pulse);
    graphics.strokeRect(-half, -half, size, size);

    const cornerSize = 2;
    graphics.fillStyle(color, alpha);
    graphics.fillRect(-half - 1, -half - 1, cornerSize, cornerSize);
    graphics.fillRect(half, -half - 1, cornerSize, cornerSize);
    graphics.fillRect(-half - 1, half, cornerSize, cornerSize);
    graphics.fillRect(half, half, cornerSize, cornerSize);

    graphics.fillStyle(0x000000, Math.min(1, alpha * 0.9));
    graphics.fillRect(-2, -2, 4, 4);
    graphics.fillStyle(color, alpha);
    graphics.fillRect(-1, -1, 2, 2);
  }

  // ============ TOOLTIP ============

  showTooltip(entry: WhirlpoolEntryData, screenX: number, screenY: number): void {
    this.hideTooltip();
    // Emit event to React component instead of creating DOM tooltip
    this.scene.events.emit('whirlpool:tooltip:show', entry);
  }

  hideTooltip(): void {
    // Emit event to React component
    this.scene.events.emit('whirlpool:tooltip:hide');
  }

  // ============ INSTANCE RENDERING ============

  enterInstance(instance: WhirlpoolInstanceData): void {
    this.isInsideWhirlpool = true;
    this.currentInstance = instance;

    // Clear overworld whirlpools from view
    Array.from(this.whirlpools.values()).forEach(sprite => {
      sprite.container.setVisible(false);
    });

    // Create instance container
    this.instanceContainer = this.scene.add.container(0, 0);
    this.instanceContainer.setDepth(10);

    // Render instance content
    this.renderInstanceBackground(instance);
    this.renderInstanceIslands(instance);
    this.renderInstanceResources(instance);
    this.renderInstanceHazards(instance);
    this.renderBossTelegraphs(instance);
    // NPCs inside whirlpools are rendered via the standard ShipRenderer
    // (server spawns them as real NPC ships scoped to the instanceId).
    this.renderExitPoint(instance);

    // Start simple animation for resource nodes (keeps pixel-art feel)
    if (!this.resourceAnimTimer) {
      this.resourceAnimTimer = this.scene.time.addEvent({
        delay: 600,
        loop: true,
        callback: () => {
          this.resourceAnimPhase = (this.resourceAnimPhase + 1) % 4;
          this.updateResourceMarkers();
        },
      });
    }

    console.log(`🌀 Entered whirlpool instance: ${instance.className}`);
  }

  exitInstance(): void {
    this.isInsideWhirlpool = false;
    this.currentInstance = null;

    // Clean up instance rendering
    if (this.instanceContainer) {
      this.instanceContainer.destroy();
      this.instanceContainer = null;
    }

    if (this.exitMarker) {
      this.exitMarker.destroy();
      this.exitMarker = null;
    }

    Array.from(this.resourceMarkers.values()).forEach(marker => {
      marker.destroy();
    });
    this.resourceMarkers.clear();

    if (this.resourceAnimTimer) {
      this.resourceAnimTimer.destroy();
      this.resourceAnimTimer = null;
    }

    Array.from(this.hazardGraphics.values()).forEach(graphics => {
      graphics.destroy();
    });
    this.hazardGraphics.clear();

    // No whirlpool NPC markers to clean up (rendered by ShipRenderer)

    // Show overworld whirlpools again
    Array.from(this.whirlpools.values()).forEach(sprite => {
      sprite.container.setVisible(true);
    });

    console.log('🌀 Exited whirlpool instance');
  }

  private renderInstanceBackground(instance: WhirlpoolInstanceData): void {
    if (!this.instanceContainer) return;

    // Create a dark, void-like background tint
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1a0a2e, 0.3); // Dark purple tint
    bg.fillRect(0, 0, instance.width, instance.height);
    this.instanceContainer.add(bg);
  }

  private renderInstanceIslands(instance: WhirlpoolInstanceData): void {
    if (!this.instanceContainer) return;

    for (const island of instance.islands) {
      const graphics = this.scene.add.graphics();
      const colors = BIOME_COLORS[island.biome] || BIOME_COLORS.void;
      
      // Draw island using tile-based rendering like main game
      const radiusTiles = Math.max(2, Math.round(island.radius / TILE_SIZE));
      const centerTileX = Math.floor(island.x / TILE_SIZE);
      const centerTileY = Math.floor(island.y / TILE_SIZE);
      
      // Seeded random for consistent tile variation
      const seed = island.x * 1000 + island.y;
      const seededRandom = (offset: number): number => {
        const x = Math.sin(seed + offset) * 10000;
        return x - Math.floor(x);
      };
      
      for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
        for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
          const dist = Math.sqrt(dx * dx + dy * dy);
          const noise = seededRandom(dx * 100 + dy) * 0.5;
          const threshold = radiusTiles * (0.8 + noise * 0.4);
          
          if (dist <= threshold) {
            const edgeDist = threshold - dist;
            let color: number;
            
            if (edgeDist < 1.2) {
              color = colors.shore;
            } else {
              color = seededRandom(dx * 30 + dy * 30) > 0.5 ? colors.main : colors.accent;
            }
            
            const worldX = (centerTileX + dx) * TILE_SIZE;
            const worldY = (centerTileY + dy) * TILE_SIZE;
            
            graphics.fillStyle(color, 1);
            graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
          }
        }
      }
      
      this.instanceContainer.add(graphics);
    }
  }

  private renderInstanceResources(instance: WhirlpoolInstanceData): void {
    for (const resource of instance.resourceNodes) {
      // Snap to tile center for crisp pixel visuals
      const tileX = Math.floor(resource.worldX / TILE_SIZE);
      const tileY = Math.floor(resource.worldY / TILE_SIZE);
      const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
      const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;

      const container = this.scene.add.container(0, 0);
      container.setDepth(60);

      const graphics = this.scene.add.graphics();
      const hitArea = this.scene.add.rectangle(worldX, worldY, TILE_SIZE * 2, TILE_SIZE * 2, 0x000000, 0);
      hitArea.setInteractive();
      hitArea.setDepth(61);

      (container as any).graphics = graphics;
      (container as any).tileX = tileX;
      (container as any).tileY = tileY;
      (container as any).resourceId = resource.id;
      (container as any).resourceType = resource.type;
      (container as any).resourceName = resource.definition?.name || 'Resource';
      (container as any).amount = resource.amount;
      (container as any).maxAmount = resource.maxAmount;

      this.drawResourceNode(graphics, resource.type, tileX, tileY, this.resourceAnimPhase);

      hitArea.on('pointerover', () => {
        const colors = RESOURCE_COLORS[resource.type] || { fill: 0x00ff88, border: 0x00aa55, icon: '[?]' };
        graphics.lineStyle(2, 0xffff00, 1);
        graphics.strokeRect(tileX * TILE_SIZE, tileY * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        this.showResourceTooltip((container as any).resourceName as string, worldX, worldY - 20, (container as any).amount as number, (container as any).maxAmount as number, colors.border);
      });

      hitArea.on('pointerout', () => {
        this.hideResourceTooltip();
        this.drawResourceNode(graphics, resource.type, tileX, tileY, this.resourceAnimPhase);
      });

      hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!pointer.leftButtonDown()) return;
        this.callbacks.onWhirlpoolResourceClick?.(resource.id, worldX, worldY);
      });

      container.add(graphics);
      container.add(hitArea);

      this.instanceContainer?.add(container);
      this.resourceMarkers.set(resource.id, container);
    }
  }

  private resourceTooltip: Phaser.GameObjects.Container | null = null;

  private showResourceTooltip(name: string, x: number, y: number, amount: number, maxAmount: number, borderColor: number): void {
    this.hideResourceTooltip();

    const container = this.scene.add.container(x, y);
    container.setDepth(200);

    const bg = this.scene.add.rectangle(0, 0, 120, 36, 0x000000, 0.85);
    bg.setOrigin(0.5);
    bg.setStrokeStyle(2, borderColor);
    container.add(bg);

    const title = this.scene.add.text(0, -8, name, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#ffffff',
    });
    title.setOrigin(0.5);
    container.add(title);

    const amountText = this.scene.add.text(0, 6, `${amount}/${maxAmount}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#87ceeb',
    });
    amountText.setOrigin(0.5);
    container.add(amountText);

    this.resourceTooltip = container;
  }

  private hideResourceTooltip(): void {
    if (this.resourceTooltip) {
      this.resourceTooltip.destroy();
      this.resourceTooltip = null;
    }
  }

  private updateResourceMarkers(): void {
    this.resourceMarkers.forEach((container) => {
      const graphics = (container as any).graphics as Phaser.GameObjects.Graphics;
      const tileX = (container as any).tileX as number;
      const tileY = (container as any).tileY as number;
      const type = (container as any).resourceType as string;
      this.drawResourceNode(graphics, type, tileX, tileY, this.resourceAnimPhase);
    });
  }

  // Pixel-art resource node rendering (1x1 tile), matching OceanScene resource nodes.
  private drawResourceNode(graphics: Phaser.GameObjects.Graphics, nodeType: string, tileX: number, tileY: number, phase: number): void {
    graphics.clear();

    const x = tileX * TILE_SIZE;
    const y = tileY * TILE_SIZE;

    const colors = RESOURCE_COLORS[nodeType] || { fill: 0x808080, border: 0x555555, icon: '[?]' };

    // Base tile
    graphics.fillStyle(colors.fill, 0.45);
    graphics.fillRect(x, y, TILE_SIZE, TILE_SIZE);
    graphics.lineStyle(1, colors.border, 1);
    graphics.strokeRect(x, y, TILE_SIZE, TILE_SIZE);

    // Simple pixel glyph (2x2) that animates slightly
    graphics.fillStyle(colors.border, 0.9);
    const p = phase % 2;
    graphics.fillRect(x + 4 + p, y + 4, 2, 2);
    graphics.fillRect(x + 9, y + 6 + p, 2, 2);

    // Center marker
    graphics.fillStyle(0xffffff, 0.8);
    graphics.fillRect(x + 7, y + 7, 2, 2);
  }

  private renderInstanceHazards(instance: WhirlpoolInstanceData): void {
    const hazardColors: Record<string, number> = {
      // Original hazards
      rogue_wave: 0x4169e1,
      whirlpool_pull: 0x00ced1,
      fog_bank: 0x708090,
      cursed_waters: 0x8b008b,
      sea_mist: 0xa9a9a9,
      // New RuneScape-inspired hazards
      tidal_surge: 0x1e90ff,
      lightning_storm: 0xffff00,
      void_rift: 0x9932cc,
      blood_pool: 0xdc143c,
      ice_barrier: 0x00ffff,
      flame_geyser: 0xff4500,
      poison_cloud: 0x32cd32,
      gravity_well: 0x4169e1,
      shadow_vortex: 0x2f4f4f,
      crystal_spikes: 0xb0e0e6,
    };

    const hazardNames: Record<string, string> = {
      // Original hazards
      rogue_wave: 'ROGUE WAVE',
      whirlpool_pull: 'WHIRLPOOL VORTEX',
      fog_bank: 'ABYSSAL FOG',
      cursed_waters: 'CURSED WATERS',
      sea_mist: 'SPECTRAL MIST',
      // New RuneScape-inspired hazards
      tidal_surge: 'TIDAL SURGE',
      lightning_storm: 'LIGHTNING STORM',
      void_rift: 'VOID RIFT',
      blood_pool: 'BLOOD POOL',
      ice_barrier: 'ICE BARRIER',
      flame_geyser: 'FLAME GEYSER',
      poison_cloud: 'POISON CLOUD',
      gravity_well: 'GRAVITY WELL',
      shadow_vortex: 'SHADOW VORTEX',
      crystal_spikes: 'CRYSTAL SPIKES',
    };

    // Tile pattern styles for visual variety (RuneScape-inspired)
    const hazardPatterns: Record<string, (x: number, y: number, dist: number, radius: number) => boolean> = {
      rogue_wave: (x, y, dist, radius) => (x + y) % 2 === 0, // Checkerboard
      whirlpool_pull: (x, y, dist, radius) => dist === radius, // Ring only
      fog_bank: (x, y, dist, radius) => true, // Solid
      cursed_waters: (x, y, dist, radius) => (x * y) % 3 === 0, // Cross pattern
      sea_mist: (x, y, dist, radius) => (x + y + Math.floor(dist)) % 2 === 0, // Diagonal
      tidal_surge: (x, y, dist, radius) => y % 2 === 0, // Horizontal stripes
      lightning_storm: (x, y, dist, radius) => x % 2 === 0, // Vertical stripes
      void_rift: (x, y, dist, radius) => (Math.abs(x) + Math.abs(y)) % 2 === 0, // Diamond
      blood_pool: (x, y, dist, radius) => dist <= radius * 0.7, // Inner circle
      ice_barrier: (x, y, dist, radius) => dist === radius || dist === radius - 1, // Double ring
      flame_geyser: (x, y, dist, radius) => (x * x + y * y) % 5 === 0, // Scattered
      poison_cloud: (x, y, dist, radius) => true, // Solid
      gravity_well: (x, y, dist, radius) => dist <= radius, // Solid circle
      shadow_vortex: (x, y, dist, radius) => (x + y * 2) % 3 === 0, // Spiral-like
      crystal_spikes: (x, y, dist, radius) => Math.abs(x) === Math.abs(y), // X pattern
    };

    for (const hazard of instance.hazards) {
      const graphics = this.scene.add.graphics();
      graphics.setDepth(5); // Below ships/NPCs but above background
      this.instanceContainer?.add(graphics);

      const color = hazardColors[hazard.type] || 0xff4444;
      const centerTileX = hazard.tileX;
      const centerTileY = hazard.tileY;
      const radiusTiles = hazard.radius;
      const pattern = hazardPatterns[hazard.type] || ((x, y) => true);

      // Draw hazard area with tile patterns and animations
      for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
        for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
          const chebyshevDist = Math.max(Math.abs(dx), Math.abs(dy));
          if (chebyshevDist > radiusTiles) continue;

          // Check if this tile matches the pattern
          if (!pattern(dx, dy, chebyshevDist, radiusTiles)) continue;

          const tileWorldX = (centerTileX + dx) * TILE_SIZE;
          const tileWorldY = (centerTileY + dy) * TILE_SIZE;

          // Fade alpha towards edge
          const distRatio = chebyshevDist / radiusTiles;
          const tileAlpha = hazard.active ? 0.2 * (1 - distRatio * 0.3) : 0.05;

          // Draw tile with border for definition
          graphics.fillStyle(color, tileAlpha);
          graphics.fillRect(tileWorldX, tileWorldY, TILE_SIZE, TILE_SIZE);
          
          // Add subtle border to each tile
          graphics.lineStyle(0.5, color, tileAlpha * 0.6);
          graphics.strokeRect(tileWorldX, tileWorldY, TILE_SIZE, TILE_SIZE);
        }
      }

      // Draw outer border ring with emphasis
      const borderAlpha = hazard.active ? 0.5 : 0.2;
      graphics.lineStyle(2, color, borderAlpha);
      const borderMinX = (centerTileX - radiusTiles) * TILE_SIZE;
      const borderMinY = (centerTileY - radiusTiles) * TILE_SIZE;
      const borderSize = (radiusTiles * 2 + 1) * TILE_SIZE;
      graphics.strokeRect(borderMinX, borderMinY, borderSize, borderSize);

      // Add corner markers for visual interest
      const cornerSize = 3;
      graphics.fillStyle(color, borderAlpha);
      const corners = [
        { x: borderMinX, y: borderMinY },
        { x: borderMinX + borderSize - cornerSize, y: borderMinY },
        { x: borderMinX, y: borderMinY + borderSize - cornerSize },
        { x: borderMinX + borderSize - cornerSize, y: borderMinY + borderSize - cornerSize },
      ];
      for (const corner of corners) {
        graphics.fillRect(corner.x, corner.y, cornerSize, cornerSize);
      }

      // Add hazard label at center
      const labelX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
      const labelY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
      const label = this.scene.add.text(labelX, labelY, hazardNames[hazard.type] || 'HAZARD', {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#' + color.toString(16).padStart(6, '0'),
        backgroundColor: '#000000dd',
        padding: { x: 4, y: 2 },
      });
      label.setOrigin(0.5);
      label.setDepth(6);
      if (this.instanceContainer) {
        this.instanceContainer.add(label);
      }

      this.hazardGraphics.set(hazard.id, graphics);
    }
  }

  private renderBossTelegraphs(instance: WhirlpoolInstanceData): void {
    // Render boss attack telegraphs with tile-based visual indicators
    const telegraphColors: Record<string, number> = {
      melee_sweep: 0xff6b6b,        // Red - close range
      ranged_volley: 0xffa500,      // Orange - projectiles
      aoe_blast: 0xffff00,          // Yellow - area explosion
      targeted_strike: 0xff1493,    // Deep pink - targeted
      area_denial: 0x9932cc,        // Dark orchid - zone control
    };

    const telegraphPatterns: Record<string, (x: number, y: number, dist: number, radius: number) => boolean> = {
      melee_sweep: (x, y, dist, radius) => dist <= radius && (x + y) % 2 === 0, // Checkerboard sweep
      ranged_volley: (x, y, dist, radius) => dist === radius, // Ring of projectiles
      aoe_blast: (x, y, dist, radius) => dist <= radius, // Solid circle
      targeted_strike: (x, y, dist, radius) => Math.abs(x) === Math.abs(y) && dist <= radius, // X pattern
      area_denial: (x, y, dist, radius) => (x + y * 2) % 3 === 0 && dist <= radius, // Spiral pattern
    };

    for (const npc of instance.npcs) {
      if (!npc.telegraphs || npc.telegraphs.length === 0) continue;
      if (npc.behavior !== 'boss') continue;

      for (const telegraph of npc.telegraphs) {
        const now = this.scene.time.now;
        const timeUntilActivation = telegraph.activatesAt - now;
        
        // Only render if telegraph is still active (not yet expired)
        if (timeUntilActivation < -telegraph.duration) continue;

        const graphics = this.scene.add.graphics();
        graphics.setDepth(4); // Below hazards but above background
        this.instanceContainer?.add(graphics);

        const color = telegraphColors[telegraph.type] || 0xff4444;
        const centerTileX = Math.floor(telegraph.centerX / TILE_SIZE);
        const centerTileY = Math.floor(telegraph.centerY / TILE_SIZE);
        const radiusTiles = telegraph.radius;
        const pattern = telegraphPatterns[telegraph.type] || ((x, y) => true);

        // Pulsing alpha based on time until activation
        const pulsePhase = (timeUntilActivation / 500) % 1;
        const baseAlpha = 0.3 + Math.sin(pulsePhase * Math.PI * 2) * 0.2;
        const isImminent = timeUntilActivation < 500; // Last 500ms before activation
        const finalAlpha = isImminent ? 0.6 + Math.sin(pulsePhase * Math.PI * 4) * 0.3 : baseAlpha;

        // Draw telegraph area with pattern
        for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
          for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
            const chebyshevDist = Math.max(Math.abs(dx), Math.abs(dy));
            if (chebyshevDist > radiusTiles) continue;

            if (!pattern(dx, dy, chebyshevDist, radiusTiles)) continue;

            const tileWorldX = (centerTileX + dx) * TILE_SIZE;
            const tileWorldY = (centerTileY + dy) * TILE_SIZE;

            // Draw tile with pulsing effect
            graphics.fillStyle(color, finalAlpha);
            graphics.fillRect(tileWorldX, tileWorldY, TILE_SIZE, TILE_SIZE);
            
            // Border for definition
            graphics.lineStyle(1, color, finalAlpha * 0.8);
            graphics.strokeRect(tileWorldX, tileWorldY, TILE_SIZE, TILE_SIZE);
          }
        }

        // Draw outer border ring with emphasis
        graphics.lineStyle(2, color, finalAlpha);
        const borderMinX = (centerTileX - radiusTiles) * TILE_SIZE;
        const borderMinY = (centerTileY - radiusTiles) * TILE_SIZE;
        const borderSize = (radiusTiles * 2 + 1) * TILE_SIZE;
        graphics.strokeRect(borderMinX, borderMinY, borderSize, borderSize);

        // Add warning indicator if imminent
        if (isImminent) {
          graphics.fillStyle(0xff0000, finalAlpha);
          const warningSize = 4;
          const corners = [
            { x: borderMinX, y: borderMinY },
            { x: borderMinX + borderSize - warningSize, y: borderMinY },
            { x: borderMinX, y: borderMinY + borderSize - warningSize },
            { x: borderMinX + borderSize - warningSize, y: borderMinY + borderSize - warningSize },
          ];
          for (const corner of corners) {
            graphics.fillRect(corner.x, corner.y, warningSize, warningSize);
          }
        }

        // Add telegraph type label
        const labelX = telegraph.centerX;
        const labelY = telegraph.centerY;
        const label = this.scene.add.text(labelX, labelY, telegraph.type.toUpperCase().replace(/_/g, ' '), {
          fontSize: '9px',
          fontFamily: 'monospace',
          color: '#' + color.toString(16).padStart(6, '0'),
          backgroundColor: '#000000dd',
          padding: { x: 3, y: 1 },
        });
        label.setOrigin(0.5);
        label.setDepth(5);
        if (this.instanceContainer) {
          this.instanceContainer.add(label);
        }
      }
    }
  }

  private renderInstanceNPCs(_instance: WhirlpoolInstanceData): void {
    // Intentionally empty: whirlpool NPCs are now real NPC ships and are rendered
    // by the standard ShipRenderer.
  }

  private renderExitPoint(instance: WhirlpoolInstanceData): void {
    const exit = instance.exitPoint;

    const tileX = Math.floor(exit.worldX / TILE_SIZE);
    const tileY = Math.floor(exit.worldY / TILE_SIZE);
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;

    const container = this.scene.add.container(worldX, worldY);
    container.setDepth(80);

    // Exit point uses same animated visual style as entry whirlpool
    const aura = this.scene.add.graphics();
    this.drawExitAura(aura, exit, this.scene.time.now);
    container.add(aura);

    const graphics = this.scene.add.graphics();
    this.drawExitVortex(graphics, exit, this.scene.time.now);
    container.add(graphics);

    (container as any).aura = aura;
    (container as any).graphics = graphics;
    (container as any).exitPoint = {
      ...exit,
      tileX,
      tileY,
      worldX,
      worldY,
    };

    // Label
    const half = Math.floor(TILE_SIZE / 2);
    const label = this.scene.add.text(0, -half - 6, 'EXIT POINT', {
      fontSize: '10px',
      fontFamily: 'monospace',
      color: '#00ffff',
      backgroundColor: '#000000cc',
      padding: { x: 2, y: 1 },
    });
    label.setOrigin(0.5, 1);
    container.add(label);

    const hitArea = this.scene.add.rectangle(0, 0, TILE_SIZE * 2, TILE_SIZE * 2, 0x000000, 0);
    hitArea.setInteractive();
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.leftButtonDown()) return;
      const rawEvent = pointer.event as MouseEvent | PointerEvent | undefined;
      rawEvent?.preventDefault?.();
      rawEvent?.stopPropagation?.();
      this.callbacks.onWhirlpoolExitClick?.(worldX, worldY);
    });
    container.add(hitArea);

    // Stability warning
    if (!exit.stable) {
      const warning = this.scene.add.text(0, half + 6, '[UNSTABLE]', {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#ffaa00',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      warning.setOrigin(0.5, 0);
      container.add(warning);
    }

    this.instanceContainer?.add(container);
    this.exitMarker = container;
  }

  private updateHazardAnimations(time: number): void {
    if (!this.currentInstance) return;

    for (const hazard of this.currentInstance.hazards) {
      const graphics = this.hazardGraphics.get(hazard.id);
      if (!graphics) continue;

      // Pulse effect
      const pulse = Math.sin(time * 0.002) * 0.2 + 0.8;
      graphics.setAlpha(hazard.active ? pulse : 0.3);
    }
  }

  // ============ INSTANCE UPDATES ============

  updateExitPoint(exitPoint: WhirlpoolInstanceData['exitPoint']): void {
    if (!this.currentInstance) return;

    this.currentInstance.exitPoint = exitPoint;

    if (this.exitMarker) {
      const tileX = Math.floor(exitPoint.worldX / TILE_SIZE);
      const tileY = Math.floor(exitPoint.worldY / TILE_SIZE);
      const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
      const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;

      this.exitMarker.setPosition(worldX, worldY);
      (this.exitMarker as any).exitPoint = {
        ...exitPoint,
        tileX,
        tileY,
        worldX,
        worldY,
      };
    }
  }

  updateResource(resourceId: string, amount: number): void {
    const marker = this.resourceMarkers.get(resourceId);
    if (marker) {
      (marker as any).amount = amount;

      const graphics = (marker as any).graphics as Phaser.GameObjects.Graphics | undefined;
      const tileX = (marker as any).tileX as number | undefined;
      const tileY = (marker as any).tileY as number | undefined;
      const type = (marker as any).resourceType as string | undefined;
      if (graphics && tileX !== undefined && tileY !== undefined && type) {
        this.drawResourceNode(graphics, type, tileX, tileY, this.resourceAnimPhase);
      }

      if (amount <= 0) {
        marker.setAlpha(0.25);
      } else {
        marker.setAlpha(1);
      }
    }
  }

  // ============ GETTERS ============

  isInWhirlpool(): boolean {
    return this.isInsideWhirlpool;
  }

  getCurrentInstance(): WhirlpoolInstanceData | null {
    return this.currentInstance;
  }

  getWhirlpoolAt(worldX: number, worldY: number): WhirlpoolEntryData | null {
    const sprites = Array.from(this.whirlpools.values());
    for (let i = 0; i < sprites.length; i++) {
      const sprite = sprites[i];
      const dx = Math.abs(sprite.data.worldX - worldX);
      const dy = Math.abs(sprite.data.worldY - worldY);
      if (dx < TILE_SIZE && dy < TILE_SIZE) {
        return sprite.data;
      }
    }
    return null;
  }

  // ============ CLEANUP ============

  destroy(): void {
    this.hideTooltip();
    
    Array.from(this.whirlpools.values()).forEach(sprite => {
      sprite.container.destroy();
    });
    this.whirlpools.clear();

    if (this.isInsideWhirlpool) {
      this.exitInstance();
    }
  }
}
